import React, { useState, useEffect, useCallback, useRef } from 'react';
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardContent, MetricsInlinePanel, type MetricsState } from '@easyflow/ui-master';
import { Activity, RefreshCw, Server, Network, Database, Video, HardDrive, Phone, Shield, Eye, ChevronDown } from 'lucide-react';
import { Button } from '@easyflow/ui-master/components/ui/button';
import type { SystemMetricsSnapshot, MetricsNodeKey } from '@easyflow/shared';
import apiClient from '@/lib/api';

// 'not_configured' (PM-024): il backend non ha l'indirizzo LAN del nodo in env → nessuna sonda, non è un guasto.
type ServerStatus = 'healthy' | 'degraded' | 'critical' | 'unreachable' | 'not_configured' | 'unknown' | 'loading';
type NodeKey = 'lb1' | 'fe1' | 'be1' | 'be2' | 'db1' | 'be3' | 'be4' | 'db2' | 'redis1' | 'fs1' | 'fs2' | 'kam1' | 'obs1';

interface ServerHealth {
  key: NodeKey;
  label: string;
  role: string;
  icon: React.ReactNode;
  status: ServerStatus;
  detail: string;
}

const statusColor: Record<ServerStatus, string> = {
  healthy: 'bg-emerald-400',
  degraded: 'bg-amber-400',
  critical: 'bg-rose-500',
  unreachable: 'bg-rose-500',
  not_configured: 'bg-slate-300 ring-1 ring-slate-400',
  unknown: 'bg-slate-400',
  loading: 'bg-slate-300 animate-pulse',
};

const statusText: Record<ServerStatus, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  critical: 'Critical',
  unreachable: 'Unreachable',
  not_configured: 'Non configurato',
  unknown: 'Unknown',
  loading: 'Checking...',
};

const NODE_META: Record<NodeKey, { label: string; role: string; icon: React.ReactNode }> = {
  lb1:    { label: 'LB-1',    role: 'Edge web pubblico',     icon: <Network   className="h-4 w-4 text-lime-400" /> },
  fe1:    { label: 'FE-1',    role: 'Nginx/Gateway',         icon: <Network   className="h-4 w-4 text-cyan-400" /> },
  // Task #3965 — 8 worker fork (porte 3000-3007)
  be1:    { label: 'BE-1',    role: 'Node.js fork x4 (x8 staged)', icon: <Server className="h-4 w-4 text-amber-400" /> },
  be2:    { label: 'BE-2',    role: 'Node.js fork x4 (x8 staged)', icon: <Server className="h-4 w-4 text-purple-400" /> },
  fs1:    { label: 'FS-1',    role: 'FreeSWITCH PBX #1',     icon: <Phone     className="h-4 w-4 text-orange-400" /> },
  fs2:    { label: 'FS-2',    role: 'FreeSWITCH PBX #2',     icon: <Phone     className="h-4 w-4 text-orange-400" /> },
  kam1:   { label: 'KAM-1',   role: 'Kamailio SBC',          icon: <Shield    className="h-4 w-4 text-indigo-400" /> },
  db1:    { label: 'DB-1',    role: 'PostgreSQL primary',    icon: <Database  className="h-4 w-4 text-emerald-400" /> },
  db2:    { label: 'DB-2',    role: 'PostgreSQL replica',    icon: <Database  className="h-4 w-4 text-teal-400" /> },
  be3:    { label: 'BE-3',    role: 'LiveKit Video #1',      icon: <Video     className="h-4 w-4 text-sky-400" /> },
  be4:    { label: 'BE-4',    role: 'LiveKit Video #2 HA',   icon: <Video     className="h-4 w-4 text-sky-400" /> },
  redis1: { label: 'REDIS-1', role: 'Redis dedicato',        icon: <HardDrive className="h-4 w-4 text-rose-400" /> },
  obs1:   { label: 'OBS-1',   role: 'Observability/bastion', icon: <Eye       className="h-4 w-4 text-slate-400" /> },
};

const ALL_KEYS: NodeKey[] = ['lb1', 'fe1', 'be1', 'be2', 'fs1', 'fs2', 'kam1', 'db1', 'db2', 'be3', 'be4', 'redis1', 'obs1'];

/**
 * Task #1922 — collector inizialmente attivo sui 5 nodi Node.
 * Task #1924 — esteso a tutti i 13 nodi (incl. LB-1) via mini-agent `system-metrics-agent`
 * (porta LAN 3099) sui restanti DB-1/DB-2/FS-1/FS-2/KAM-1/REDIS-1/OBS-1.
 */
const METRICS_NODES: ReadonlySet<NodeKey> = new Set<NodeKey>([
  'lb1', 'fe1', 'be1', 'be2', 'be3', 'be4',
  'db1', 'db2', 'fs1', 'fs2', 'kam1', 'redis1', 'obs1',
]);

const METRICS_POLL_MS = 5000;

function buildLoading(): ServerHealth[] {
  return ALL_KEYS.map((k) => ({
    key: k,
    label: NODE_META[k].label,
    role: NODE_META[k].role,
    icon: NODE_META[k].icon,
    status: 'loading' as ServerStatus,
    detail: 'Controllo in corso...',
  }));
}

function mapStatus(s: string | undefined): ServerStatus {
  if (!s) return 'unknown';
  if (s === 'healthy' || s === 'degraded' || s === 'critical' || s === 'unreachable' || s === 'not_configured' || s === 'unknown') return s;
  return 'unknown';
}

interface InfraServers {
  lb1?: { status?: string; probe?: string; latency_ms?: number | null; detail?: string };
  fe1?: { status?: string; nginx?: { active_connections?: number; status?: string } };
  be1?: { status?: string; cpu?: { usage?: number }; memory?: { percentage?: number }; freeswitch?: { state?: string } };
  be2?: { status?: string; latency_ms?: number; error?: string };
  db1?: { status?: string; db_size_gb?: string; active_connections?: number; query_latency_ms?: number; error?: string };
  be3?: { status?: string; probe?: string; latency_ms?: number | null; detail?: string };
  be4?: { status?: string; probe?: string; latency_ms?: number | null; detail?: string };
  db2?: { status?: string; state?: string | null; sync_state?: string | null; lag_bytes?: number | null; tcp_latency_ms?: number | null; detail?: string };
  redis1?: { status?: string; ping?: 'PONG' | null; latency_ms?: number | null; version?: string | null; detail?: string };
  fs1?: { status?: string; probe?: string; latency_ms?: number | null; port?: number; detail?: string };
  fs2?: { status?: string; probe?: string; latency_ms?: number | null; port?: number; detail?: string };
  kam1?: { status?: string; probe?: string; latency_ms?: number | null; port?: number; detail?: string };
  obs1?: { status?: string; probe?: string; latency_ms?: number | null; port?: number; detail?: string };
}

interface InfraResponse {
  overall?: string;
  servers?: InfraServers;
}

interface MetricsResponseData {
  node: MetricsNodeKey;
  metrics: SystemMetricsSnapshot;
}

function detailFor(key: NodeKey, s: InfraServers): string {
  switch (key) {
    case 'fe1': {
      const n = s.fe1?.nginx;
      if (n?.active_connections != null) return `${n.active_connections} conn attive`;
      return s.fe1?.status === 'unreachable' ? 'Nginx non raggiungibile' : 'Nginx OK';
    }
    case 'be1': {
      const b = s.be1;
      if (!b) return 'Non raggiungibile';
      return `CPU ${b.cpu?.usage ?? '?'}% · RAM ${b.memory?.percentage ?? '?'}% · FS ${b.freeswitch?.state ?? '?'}`;
    }
    case 'be2':
      return s.be2?.latency_ms != null
        ? `Risposta ${s.be2.latency_ms}ms`
        : s.be2?.error ?? 'Non raggiungibile';
    case 'db1': {
      const d = s.db1;
      if (d?.db_size_gb) return `${d.db_size_gb} GB · ${d.active_connections ?? '?'} conn · ${d.query_latency_ms ?? '?'}ms`;
      return d?.error ?? 'Non raggiungibile';
    }
    case 'be3':
    case 'be4':
      return s[key]?.detail ?? 'Probe non disponibile';
    case 'db2':
      return s.db2?.detail ?? 'Probe non disponibile';
    case 'redis1':
      return s.redis1?.detail ?? 'Probe non disponibile';
    case 'fs1':
    case 'fs2':
    case 'kam1':
    case 'obs1':
    case 'lb1':
      return s[key]?.detail ?? 'Probe non disponibile';
  }
}

export function HealthMonitorWidget() {
  const [servers, setServers] = useState<ServerHealth[]>(buildLoading());
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [overall, setOverall] = useState<string>('loading');
  const [expandedKey, setExpandedKey] = useState<NodeKey | null>(null);
  const [metricsState, setMetricsState] = useState<MetricsState>({ kind: 'loading' });
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inflightCtrlRef = useRef<AbortController | null>(null);

  const checkHealth = async () => {
    setRefreshing(true);
    try {
      const res = await apiClient.get<InfraResponse>('/master/infrastructure/health');
      if (res.success && res.data) {
        const d = res.data;
        const s: InfraServers = d.servers ?? {};
        setOverall(d.overall ?? 'unknown');
        setServers(ALL_KEYS.map((k) => ({
          key: k,
          label: NODE_META[k].label,
          role: NODE_META[k].role,
          icon: NODE_META[k].icon,
          status: mapStatus(s[k]?.status),
          detail: detailFor(k, s),
        })));
        setLastUpdate(new Date());
      }
    } catch {
      setServers((prev) => prev.map((srv) => ({ ...srv, status: 'unreachable', detail: 'Errore di rete' })));
      setOverall('critical');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNodeMetrics = useCallback(async (node: NodeKey) => {
    if (!METRICS_NODES.has(node)) return;
    if (inflightCtrlRef.current) inflightCtrlRef.current.abort();
    const ctrl = new AbortController();
    inflightCtrlRef.current = ctrl;
    try {
      const res = await apiClient.get<MetricsResponseData>(`/master/infrastructure/metrics?node=${node}`);
      if (ctrl.signal.aborted) return;
      if (res.success && res.data?.metrics) {
        setMetricsState({ kind: 'ready', data: res.data.metrics, fetchedAt: Date.now() });
      } else {
        setMetricsState({ kind: 'error', message: res.error || 'Risposta non valida' });
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setMetricsState({ kind: 'error', message: err instanceof Error ? err.message : 'Errore di rete' });
    }
  }, []);

  const toggleExpand = useCallback((key: NodeKey) => {
    setExpandedKey((prev) => {
      const next = prev === key ? null : key;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      if (inflightCtrlRef.current) { inflightCtrlRef.current.abort(); inflightCtrlRef.current = null; }
      if (next === null) {
        setMetricsState({ kind: 'loading' });
        return null;
      }
      if (!METRICS_NODES.has(next)) {
        setMetricsState({ kind: 'unsupported' });
        return next;
      }
      setMetricsState({ kind: 'loading' });
      void fetchNodeMetrics(next);
      pollRef.current = setInterval(() => { void fetchNodeMetrics(next); }, METRICS_POLL_MS);
      return next;
    });
  }, [fetchNodeMetrics]);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (inflightCtrlRef.current) inflightCtrlRef.current.abort();
    };
  }, []);

  const overallColor = overall === 'healthy' ? 'text-emerald-400' : overall === 'degraded' ? 'text-amber-400' : 'text-rose-400';
  const overallDot = overall === 'healthy' ? 'bg-emerald-400' : overall === 'degraded' ? 'bg-amber-400' : 'bg-rose-400';

  return (
    <GlassCard variant="elevated" className="h-full">
      <GlassCardHeader className="flex flex-row items-center justify-between p-4 pb-2">
        <div className="flex items-center gap-2">
          <Activity className={`h-5 w-5 ${overall === 'healthy' ? 'text-emerald-400' : 'text-amber-400'}`} />
          <GlassCardTitle className="text-base">Infrastructure Health · {ALL_KEYS.length} nodi</GlassCardTitle>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{lastUpdate ? lastUpdate.toLocaleTimeString() : ''}</span>
          <Button variant="ghost" size="sm" onClick={checkHealth} disabled={refreshing} className="h-7 w-7 p-0">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </GlassCardHeader>
      <GlassCardContent className="p-4 pt-2">
        <div className="space-y-2">
          {servers.map((server) => {
            const isExpanded = expandedKey === server.key;
            return (
              <div
                key={server.key}
                className="rounded-lg bg-muted/50 dark:bg-white/5 border border-slate-100 dark:border-white/10 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(server.key)}
                  aria-expanded={isExpanded}
                  aria-controls={`metrics-panel-${server.key}`}
                  className="w-full flex items-center gap-3 p-2 text-left hover:bg-white/40 dark:hover:bg-white/5 transition-colors"
                >
                  <div className="p-1.5 rounded-md bg-muted shrink-0">{server.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-foreground">{server.label}</span>
                      <span className="text-xs text-muted-foreground dark:text-muted-foreground/70 truncate">{server.role}</span>
                    </div>
                    <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 truncate mt-0.5">{server.detail}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <div className={`w-2.5 h-2.5 rounded-full ${statusColor[server.status]}`} />
                    <span className={`text-xs font-medium ${
                      server.status === 'healthy' ? 'text-emerald-600 dark:text-emerald-400' :
                      server.status === 'degraded' ? 'text-amber-600 dark:text-amber-400' :
                      server.status === 'loading' ? 'text-muted-foreground' :
                      server.status === 'unknown' ? 'text-muted-foreground' :
                      server.status === 'not_configured' ? 'text-muted-foreground' :
                      'text-rose-600 dark:text-rose-400'
                    }`}>
                      {statusText[server.status]}
                    </span>
                    <ChevronDown
                      className={`h-3.5 w-3.5 text-muted-foreground/70 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </div>
                </button>
                <div
                  id={`metrics-panel-${server.key}`}
                  className={`overflow-hidden transition-[max-height,opacity] duration-300 ${isExpanded ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  {isExpanded && (
                    <div className="px-3 pb-3 border-t border-slate-100 dark:border-white/10">
                      <MetricsInlinePanel state={metricsState} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-3 border-t border-border dark:border-white/10">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground dark:text-muted-foreground/70">Stato complessivo</span>
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${overallDot}`} />
              <span className={`text-sm font-medium ${overallColor} capitalize`}>
                {overall === 'loading' ? 'Controllo...' : overall}
              </span>
            </div>
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}
